// client/src/pages/School_Admin/Finance/BudgetsPage.js
//
// BUDGETS — what a department is allowed to spend in a year, and what it has.
//
// REWRITTEN, because the page that stood here described a system that does not
// exist. Its form collected name / academicYear / totalAmount / description /
// startDate / endDate / status and posted them to /budgets, where the model has
// department, year and amount — two of them NOT NULL and none of them sent, so
// every save failed with a database error. It also posted allocations to
// /budgets/:id/allocations, a route that has never existed, and drew utilisation
// from a `spent` column that no code anywhere has ever written to, so every bar
// read zero. None of it could work; keeping its shape would only have preserved
// the impression that it did.
//
// What a budget actually is here: a DEPARTMENT, a YEAR, an AMOUNT. What has been
// spent is derived by the server from the expenses filed under a category of the
// same name, so this page and the Expenses page cannot disagree about the same
// money, and nothing has to be kept up to date by hand.
//
// The consequence is stated on screen rather than hidden: a budget only tracks
// spending once expenses are filed under a matching category name. A budget
// sitting at 0% may be untouched, or may simply be spelled differently from the
// category the money went to — and the reader cannot tell those apart unless the
// page says so.
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Card, CardContent, Button, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select,
  MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Chip, IconButton, LinearProgress, Alert, Tooltip, Snackbar,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import AppLoader from '../../../components/AppLoader';
import { fetchJson } from '../../../utils/fetchJson';

const XAF = (n) => new Intl.NumberFormat('en-CM', {
  style: 'currency', currency: 'XAF', minimumFractionDigits: 0,
}).format(Number(n || 0));

const emptyForm = () => ({
  department: '', year: new Date().getFullYear(), amount: '',
});

export default function BudgetsPage() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [year, setYear] = useState(currentYear);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Same rule as the Financial Reports filter: the years the school actually
  // uses, plus next year so a budget can be set before it starts.
  const yearOptions = useMemo(() => {
    const set = new Set(rows.map((r) => Number(r.year)).filter(Boolean));
    set.add(currentYear);
    set.add(currentYear + 1);
    for (let i = 0; i < 4; i += 1) set.add(currentYear - i);
    return [...set].sort((a, b) => b - a);
  }, [rows, currentYear]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [list, cats] = await Promise.all([
        fetchJson(`/api/budgets?year=${encodeURIComponent(year)}`, { method: 'GET' }),
        // Offered as suggestions so a department can be named to match a
        // category that already exists — which is what makes spending track.
        fetchJson('/api/expense-categories', { method: 'GET' }).catch(() => []),
      ]);
      setRows(Array.isArray(list) ? list : (list?.data || []));
      setCategories(Array.isArray(cats) ? cats : (cats?.data || []));
    } catch (e) {
      setError(e?.message || t('budgetsPage.errors.loadData', 'Could not load the budgets.'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [year]);

  const totals = useMemo(() => {
    let allocated = 0; let spent = 0; let over = 0;
    for (const b of rows) {
      allocated += Number(b.amount || 0);
      spent += Number(b.spent || 0);
      if (b.overspent) over += 1;
    }
    return {
      allocated, spent, remaining: allocated - spent, overspent: over,
      utilisation: allocated > 0 ? Math.round((spent / allocated) * 1000) / 10 : 0,
    };
  }, [rows]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm(), year });
    setDialogOpen(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({ department: b.department || '', year: b.year || year, amount: String(b.amount ?? '') });
    setDialogOpen(true);
  };

  const problem = (() => {
    if (!String(form.department || '').trim()) return t('budgetsPage.needs.department', 'Name the department.');
    const y = Number(form.year);
    if (!Number.isFinite(y) || y < 2000 || y > 2100) return t('budgetsPage.needs.year', 'Choose a year.');
    if (!(Number(form.amount) > 0)) return t('budgetsPage.needs.amount', 'Enter an amount above zero.');
    return '';
  })();

  const save = async () => {
    if (problem) { setError(problem); return; }
    setSaving(true);
    setError('');
    try {
      await fetchJson(`/api/budgets${editing ? `/${editing.id}` : ''}`, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          department: String(form.department).trim(),
          year: Number(form.year),
          amount: Number(form.amount),
        }),
      });
      setDialogOpen(false);
      setEditing(null);
      setSnack(editing
        ? t('budgetsPage.snack.updated', 'Budget updated.')
        : t('budgetsPage.snack.created', 'Budget created.'));
      // A budget saved for another year belongs in that year's view.
      if (Number(form.year) !== Number(year)) setYear(Number(form.year)); else await load();
    } catch (e) {
      setError(e?.data?.error || e?.message || t('budgetsPage.errors.saveBudget', 'Could not save the budget.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await fetchJson(`/api/budgets/${confirmDelete.id}`, { method: 'DELETE' });
      setConfirmDelete(null);
      setSnack(t('budgetsPage.snack.deleted', 'Budget removed.'));
      await load();
    } catch (e) {
      setError(e?.data?.error || e?.message || t('budgetsPage.errors.deleteBudget', 'Could not remove the budget.'));
    } finally {
      setSaving(false);
    }
  };

  const barColour = (b) => (b.overspent ? 'error' : Number(b.utilisation) >= 90 ? 'warning' : 'success');

  return (
    <Box p={2}>
      <Typography variant="body2" color="text.secondary">
        {t('budgetsPage.purpose', 'Set what each department may spend in a year. Spending is counted from the expenses filed under a matching category.')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mt: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="budget-year">{t('budgetsPage.year', 'Year')}</InputLabel>
          <Select
            labelId="budget-year"
            label={t('budgetsPage.year', 'Year')}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {yearOptions.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </FormControl>

        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          {t('budgetsPage.newBudget', 'Add a budget')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <AppLoader variant="circular3" size={58} centered minHeight="40vh" />
      ) : (
        <>
          {/* Read off the rows below, so the summary cannot disagree with them. */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('budgetsPage.allocated', 'Allocated')}</Typography>
                <Typography variant="h6">{XAF(totals.allocated)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('budgetsPage.spent', 'Spent')}</Typography>
                <Typography variant="h6">{XAF(totals.spent)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('budgetsPage.remaining', 'Remaining')}</Typography>
                <Typography variant="h6" color={totals.remaining < 0 ? 'error.main' : 'inherit'}>
                  {XAF(totals.remaining)}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 180, flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('budgetsPage.utilisation', 'Used')}: {totals.utilisation}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(totals.utilisation, 100)}
                  color={totals.remaining < 0 ? 'error' : totals.utilisation >= 90 ? 'warning' : 'success'}
                  sx={{ height: 8, borderRadius: 4, mt: 1 }}
                />
              </Box>
            </Box>
            {totals.overspent > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t('budgetsPage.overspentWarning', {
                  count: totals.overspent,
                  defaultValue: '{{count}} budget(s) have been overspent.',
                })}
              </Alert>
            )}
          </Paper>

          {rows.length === 0 ? (
            <Card><CardContent>
              <Typography variant="subtitle1">{t('budgetsPage.empty', 'No budgets for this year')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('budgetsPage.emptyBody', 'Add one per department. Name it exactly as the expense category the department spends under, so its spending is counted.')}
              </Typography>
            </CardContent></Card>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('budgetsPage.columns.department', 'Department')}</TableCell>
                    <TableCell align="right">{t('budgetsPage.columns.amount', 'Budget')}</TableCell>
                    <TableCell align="right">{t('budgetsPage.columns.spent', 'Spent')}</TableCell>
                    <TableCell align="right">{t('budgetsPage.columns.remaining', 'Remaining')}</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>{t('budgetsPage.columns.used', 'Used')}</TableCell>
                    <TableCell align="right">{t('budgetsPage.columns.actions', 'Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((b) => {
                    const known = categories.some(
                      (c) => String(c.name || '').trim().toLowerCase() === String(b.department || '').trim().toLowerCase(),
                    );
                    return (
                      <TableRow key={b.id}>
                        <TableCell>
                          {b.department}
                          {/* Zero spending has two very different causes. Say which. */}
                          {!known && (
                            <Tooltip title={t('budgetsPage.noCategoryHint', 'No expense category has this name, so nothing is counted against this budget yet.')}>
                              <Chip size="small" sx={{ ml: 1 }} label={t('budgetsPage.noCategory', 'no matching category')} />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">{XAF(b.amount)}</TableCell>
                        <TableCell align="right">{XAF(b.spent)}</TableCell>
                        <TableCell align="right" sx={{ color: Number(b.remaining) < 0 ? 'error.main' : 'inherit' }}>
                          {XAF(b.remaining)}
                        </TableCell>
                        <TableCell>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(Number(b.utilisation || 0), 100)}
                            color={barColour(b)}
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {b.utilisation}%{b.overspent ? ` — ${t('budgetsPage.overspent', 'overspent')}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEdit(b)}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => setConfirmDelete(b)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {editing
            ? t('budgetsPage.editBudget', 'Edit budget')
            : t('budgetsPage.newBudget', 'Add a budget')}
        </DialogTitle>
        <DialogContent>
          <TextField
            label={t('budgetsPage.columns.department', 'Department')}
            fullWidth sx={{ mt: 2 }}
            value={form.department}
            onChange={(e) => setForm({ ...form, department: e.target.value })}
            helperText={t('budgetsPage.departmentHelp', 'Match an expense category name so this budget tracks that spending.')}
            required
          />
          {categories.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {categories.map((c) => (
                <Chip
                  key={c.id}
                  size="small"
                  label={c.name}
                  onClick={() => setForm({ ...form, department: c.name })}
                  variant={form.department === c.name ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          )}

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="budget-form-year">{t('budgetsPage.year', 'Year')}</InputLabel>
            <Select
              labelId="budget-form-year"
              label={t('budgetsPage.year', 'Year')}
              value={form.year}
              onChange={(e) => setForm({ ...form, year: Number(e.target.value) })}
            >
              {yearOptions.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField
            label={t('budgetsPage.columns.amount', 'Budget')}
            type="number"
            fullWidth sx={{ mt: 2 }}
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            InputProps={{ inputProps: { min: 0 } }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            {t('actions.cancel')}
          </Button>
          <Button variant="contained" onClick={save} disabled={saving || Boolean(problem)}>
            {t('actions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmDelete)} onClose={() => !saving && setConfirmDelete(null)}>
        <DialogTitle>{t('budgetsPage.deleteTitle', 'Remove this budget')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('budgetsPage.deleteBody', {
              department: confirmDelete?.department || '',
              year: confirmDelete?.year || '',
              defaultValue: 'The {{year}} budget for {{department}} will be removed. The expenses recorded against it are not affected.',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={saving}>{t('actions.cancel')}</Button>
          <Button color="error" variant="contained" onClick={remove} disabled={saving}>
            {t('actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={3000}
        onClose={() => setSnack('')}
        message={snack}
      />
    </Box>
  );
}
